import { readTransactionMoney } from '../chunk-XMALDZBY.js';
import { roleLabel, sagaIdLabel, canonRole, roleSide } from '../chunk-7OQI3M2G.js';
export { ACTIVITY_SAGA_KINDS, CANON_DESTINATION, CANON_SOURCE, CUSTODY_SAGA_KINDS, DEFAULT_COMPLETE_ROLES, FINDING_SAGA_KINDS, GROUP_CARD_TITLES, GROUP_RELATIONSHIP_LABELS, GROUP_SHAPE_LABELS, GROUP_SHAPE_MEANING, GROUP_STATE_MEANING, OPEN_ENDED_RELATIONSHIP_TYPES, OPEN_ENDED_SAGA_KINDS, OPEN_ENDED_SAGA_PREFIXES, POSITION_SAGA_KINDS, ROLE_COMPLEMENT, ROLE_LABELS, ROLE_RANK, SAGA_ID_LABELS, SAGA_LABELS, UNKNOWN_ROLE_RANK, canonRole, compareGroupCards, compareSagas, correlationIdText, expectedMissingRoles, groupCardCategory, groupRelationshipLabel, groupShapeLabel, groupShapeMeaning, groupStateLabel, groupStateMeaning, groupTransactionCount, isFindingSaga, isOpenEndedGroup, isReconsiderableState, isReopenableState, isSoleMemberActivity, isWatchingGroupState, peelChainLabel, presentRoles, prettifyToken, reconcileSagas, refsFromGroups, roleLabel, roleRank, roleSide, rowStatesFromSagas, sagaIdLabel, sagaKindsByShape, sagaLabel, sagasFromRefs, shortCorrelationId, titleRole } from '../chunk-7OQI3M2G.js';
import { formatRelativeTime, formatTimestamp } from '../chunk-VEM22REL.js';
import '../chunk-22ORTNYR.js';
import '../chunk-D5AWCYWA.js';
import '../chunk-XR6TNFQL.js';
import { unwrapData } from '../chunk-HIWBYL4O.js';
import '../chunk-CH4WKQ26.js';
import '../chunk-RMLZ5HCT.js';
import '../chunk-BRBMP7SU.js';
import '../chunk-MZH5OJVQ.js';
import '../chunk-5WIUKZF6.js';
import '../chunk-HCZGW64N.js';
import '../chunk-77DGXD3H.js';
import '../chunk-KVAN7JA4.js';
import { rowsFromWire, isRecord } from '../chunk-BFUJOKOR.js';
import '../chunk-EE4CQDCZ.js';
import '../chunk-6B5KO6TH.js';
import '../chunk-3QN2VZ56.js';
import '../chunk-LNDNXOJP.js';
import '../chunk-LVCDLPYA.js';
import '../chunk-UEXNJL5R.js';
import { isIngestableChain } from '../chunk-TMSQZ2EI.js';
import '../chunk-QDL7G4J2.js';
import '../chunk-O2TUCS6S.js';
import '../chunk-WMKTNRKP.js';
import '../chunk-43SJKO57.js';
import { toCanonicalChainId, chainDisplayName } from '../chunk-WRZGIIO2.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-LD6PO66H.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-O5TCTRWM.js';
import '../chunk-O3OIBNP7.js';
import '../chunk-PZ5AY32C.js';
import { z } from 'zod';

var GROUPING_COMPARATORS = [
  "eq",
  "neq",
  "in",
  "nin",
  "contains",
  "gt",
  "gte",
  "lt",
  "lte",
  "exists",
  "missing"
];
var GROUPING_SCOPES = ["default", "org", "tenant", "user"];
var conditionSchema = z.object({
  /** Field path on a {@link TxFact}, e.g. `method`, `valueUsd`, `tags`. */
  field: z.string().trim().min(1),
  op: z.enum(GROUPING_COMPARATORS),
  /** Comparison operand. Omitted for `exists`/`missing`. */
  value: z.unknown().optional()
});
var stepSchema = z.object({
  id: z.string().trim().min(1),
  label: z.string().trim().optional(),
  /** All conditions must hold (AND). An empty list matches any transaction. */
  match: z.array(conditionSchema).default([])
});
var correlationSchema = z.object({
  /** Fields whose values must be equal across every matched tx in a group. */
  equalFields: z.array(z.string().trim().min(1)).optional()
});
var GroupingRuleSchema = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1, "Name is required").max(120),
  description: z.string().trim().max(2e3).optional(),
  enabled: z.boolean().default(true),
  scope: z.enum(GROUPING_SCOPES).default("user"),
  /** Ordered steps; a group must satisfy them in sequence. */
  steps: z.array(stepSchema).min(1, "At least one step is required"),
  correlation: correlationSchema.optional(),
  /** Max elapsed time (ms) between the first and last step. 0/omitted = unbounded. */
  windowMs: z.number().int().nonnegative().optional(),
  /** Require the matched steps to span more than one chain. */
  crossChain: z.boolean().default(false),
  /** Optional colour for the group badge/edge in the graph. */
  color: z.string().trim().optional()
});
function validateGroupingRule(rule) {
  const result = GroupingRuleSchema.safeParse(rule);
  if (result.success) return { success: true, data: result.data, fieldErrors: {} };
  const flat = result.error.flatten();
  return {
    success: false,
    fieldErrors: flat.fieldErrors,
    message: flat.formErrors[0]
  };
}
function approveThenSendRule(overrides = {}) {
  return GroupingRuleSchema.parse({
    id: overrides.id ?? "approve-then-send",
    name: overrides.name ?? "Approve then send",
    description: overrides.description ?? "An approval followed by a transfer from the same address \u2014 the classic two-step settlement pattern.",
    crossChain: overrides.crossChain ?? true,
    correlation: overrides.correlation ?? { equalFields: ["from"] },
    windowMs: overrides.windowMs,
    steps: overrides.steps ?? [
      { id: "approve", label: "Approve", match: [{ field: "method", op: "eq", value: "approve" }] },
      {
        id: "send",
        label: "Send",
        match: [{ field: "method", op: "in", value: ["transfer", "transferFrom", "send"] }]
      }
    ],
    ...overrides
  });
}

// src/grouping/evaluate.ts
function fieldValue(tx, field) {
  if (field in tx) return tx[field];
  if (field.includes(".")) {
    let cur = tx;
    for (const part of field.split(".")) {
      if (cur == null || typeof cur !== "object") return void 0;
      cur = cur[part];
    }
    return cur;
  }
  return void 0;
}
function asNumber(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
}
function compare(actual, op, expected) {
  switch (op) {
    case "exists":
      return actual != null;
    case "missing":
      return actual == null;
    case "eq":
      return actual === expected;
    case "neq":
      return actual !== expected;
    case "in":
      return Array.isArray(expected) && expected.includes(actual);
    case "nin":
      return Array.isArray(expected) && !expected.includes(actual);
    case "contains": {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === "string") return actual.includes(String(expected));
      return false;
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = asNumber(actual);
      const b = asNumber(expected);
      if (a === void 0 || b === void 0) return false;
      return op === "gt" ? a > b : op === "gte" ? a >= b : op === "lt" ? a < b : a <= b;
    }
    default:
      return false;
  }
}
function conditionHolds(tx, condition) {
  return compare(fieldValue(tx, condition.field), condition.op, condition.value);
}
function stepMatches(tx, step) {
  return step.match.every((c) => conditionHolds(tx, c));
}
function correlationKey(tx, equalFields) {
  if (!equalFields || equalFields.length === 0) return "";
  return equalFields.map((f) => `${f}=${String(fieldValue(tx, f) ?? "")}`).join("&");
}
function evaluateRule(rule, txs) {
  if (rule.enabled === false || rule.steps.length === 0) return [];
  const equalFields = rule.correlation?.equalFields;
  const partitions = /* @__PURE__ */ new Map();
  for (const tx of txs) {
    const key = correlationKey(tx, equalFields);
    let bucket = partitions.get(key);
    if (!bucket) {
      bucket = [];
      partitions.set(key, bucket);
    }
    bucket.push(tx);
  }
  const groups = [];
  for (const [key, partTxs] of partitions) {
    const sorted = [...partTxs].sort((a, b) => a.timestamp - b.timestamp);
    let current = [];
    let stepIdx = 0;
    const reset = () => {
      current = [];
      stepIdx = 0;
    };
    for (const tx of sorted) {
      if (current.length > 0 && rule.windowMs && rule.windowMs > 0) {
        const anchor = current[0].tx.timestamp;
        if (tx.timestamp - anchor > rule.windowMs) {
          reset();
        }
      }
      const step = rule.steps[stepIdx];
      if (stepMatches(tx, step)) {
        current.push({ stepId: step.id, tx });
        stepIdx += 1;
        if (stepIdx === rule.steps.length) {
          const group = finalizeGroup(rule, key, current);
          if (group) groups.push(group);
          reset();
        }
      } else if (stepIdx > 0 && stepMatches(tx, rule.steps[0])) {
        current = [{ stepId: rule.steps[0].id, tx }];
        stepIdx = 1;
      }
    }
  }
  return groups;
}
function finalizeGroup(rule, key, matches) {
  const chainIds = [...new Set(matches.map((m) => m.tx.chainId))];
  if (rule.crossChain && chainIds.length < 2) return null;
  const times = matches.map((m) => m.tx.timestamp);
  const spanMs = Math.max(...times) - Math.min(...times);
  return { ruleId: rule.id, key, matches, chainIds, spanMs };
}
function evaluateRules(rules, txs) {
  return rules.flatMap((rule) => evaluateRule(rule, txs));
}

// src/grouping/reach.ts
function expectedRemoteChains(legs) {
  const present = /* @__PURE__ */ new Set();
  for (const leg of legs) {
    const chain = toCanonicalChainId(leg.txChain);
    if (chain) present.add(chain);
  }
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const leg of legs) {
    const { hops } = readTransactionMoney({
      txId: leg.txId,
      chainId: leg.txChain,
      valueScene: leg.scene,
      semanticScene: leg.semanticScene ?? null
    });
    for (const hop of hops) {
      const chain = toCanonicalChainId(hop.remoteChain);
      if (!chain || present.has(chain) || seen.has(chain)) continue;
      seen.add(chain);
      out.push(chain);
    }
  }
  return out;
}
var LEG_SOURCE_KIND = "tx_hashes";
var UNKNOWN_REACH = Object.freeze({ kind: "unknown", chain: null });
function legReach(chains) {
  const uncovered = chains.find((chain) => chain && !isIngestableChain(chain, LEG_SOURCE_KIND));
  if (uncovered) return { kind: "uncovered", chain: toCanonicalChainId(uncovered) ?? uncovered };
  const first = chains.find(Boolean);
  if (first) return { kind: "fetchable", chain: toCanonicalChainId(first) ?? first };
  return UNKNOWN_REACH;
}
function missingLegReason(reach, { confirmed = false } = {}) {
  const where = reach.chain ? chainDisplayName(reach.chain) : "";
  switch (reach.kind) {
    case "uncovered":
      return `is expected on ${where}, which no configured provider indexes \u2014 a run cannot fetch it, even by hash. Delivery is unverified here; check the protocol explorer for its status.`;
    case "fetchable":
      return confirmed ? `has not arrived on ${where}` : `is expected on ${where} \u2014 add it by hash or ingest its address on the Scope tab, then pin it here`;
    default:
      return confirmed ? "has not arrived" : "not among this case's transactions";
  }
}
function missingLegLabel(reach, options = {}) {
  const where = reach.chain ? chainDisplayName(reach.chain) : "";
  if (reach.kind === "uncovered") return `${where} not indexed`;
  if (reach.kind === "fetchable") return options.confirmed ? `awaiting ${where}` : `not in this set (${where})`;
  return options.confirmed ? "awaiting" : "not in this set";
}

// src/grouping/expectedLeg.ts
var SIDE_CHAIN_FACT = {
  destination: "dest_chain",
  origin: "source_chain"
};
var DELIVERY_FACTS = {
  destination: { recipient: "recipient", amount: "dest_amount", minimum: "dest_amount_minimum" }
};
function soleFact(highlights, key) {
  const values = /* @__PURE__ */ new Set();
  for (const group of highlights) {
    for (const attribute of group.attributes) {
      if (attribute.key !== key || typeof attribute.value !== "string") continue;
      const value = attribute.value.trim();
      if (value) values.add(value);
    }
  }
  return values.size === 1 ? [...values][0] : void 0;
}
function withUnit(value) {
  return value && !/^\d+$/.test(value) ? value : void 0;
}
function deliveryFor(highlights, role) {
  const side = roleSide(role);
  const keys = side ? DELIVERY_FACTS[side] : void 0;
  if (!keys) return void 0;
  const recipient = soleFact(highlights, keys.recipient);
  const exact = withUnit(soleFact(highlights, keys.amount));
  const floor = exact ? void 0 : withUnit(soleFact(highlights, keys.minimum));
  const amount = exact ?? floor;
  if (!recipient && !amount) return void 0;
  return {
    ...recipient ? { recipient } : {},
    ...amount ? { amount } : {},
    minimum: Boolean(floor)
  };
}
var NO_CHAINS = /* @__PURE__ */ new Set();
function canonicalChains(values, except = NO_CHAINS) {
  const out = [];
  for (const value of values) {
    if (typeof value !== "string" || !value.trim()) continue;
    const chain = toCanonicalChainId(value) ?? value;
    if (except.has(chain) || out.includes(chain)) continue;
    out.push(chain);
  }
  return out;
}
function isExpectedNode(node) {
  return node.attributes?.expected === true || node.badge === "expected";
}
function nodeRole(node) {
  const role = node.attributes?.expected_role ?? node.attributes?.saga_role;
  return canonRole(typeof role === "string" ? role : "");
}
function declaredFor(held, targetRole, expectedRoles) {
  const side = roleSide(targetRole);
  const pair = [...new Set((expectedRoles ?? []).map(canonRole).filter(Boolean))];
  const directed = [];
  const undirected = [];
  for (const { chain, role } of held.declared) {
    const from = roleSide(role);
    if (side && from) {
      if (from !== side) directed.push(chain);
    } else if (pair.length === 2 && pair.includes(targetRole) && pair.includes(canonRole(role)) && canonRole(role) !== targetRole) {
      directed.push(chain);
    } else {
      undirected.push(chain);
    }
  }
  const stated = canonicalChains(directed);
  for (const chain of canonicalChains(undirected, held.chains)) {
    if (!stated.includes(chain)) stated.push(chain);
  }
  return stated;
}
function chainsFor(held, role, expectedRoles) {
  const side = roleSide(role);
  const stamped = canonicalChains(
    held.scenes.flatMap((scene) => scene?.nodes ?? []).filter((node) => isExpectedNode(node) && nodeRole(node) === role).map((node) => node.attributes?.expected_chain_id)
  );
  if (stamped.length) return { chains: stamped, basis: "stamped" };
  const fromProtocol = declaredFor(held, role, expectedRoles);
  if (fromProtocol.length) return { chains: fromProtocol, basis: "declared" };
  const hops = expectedRemoteChains(held.hops).filter((chain) => !held.chains.has(chain));
  if (hops.length) return { chains: hops, basis: "decoded" };
  const factKey = side ? SIDE_CHAIN_FACT[side] : void 0;
  const facts = factKey ? canonicalChains(
    held.highlights.flatMap(
      (h) => h.attributes.filter((a) => a.key === factKey).map((a) => a.value)
    )
  ) : [];
  if (facts.length) return { chains: facts, basis: "decoded" };
  return { chains: [] };
}
function heldChains(chainIds) {
  const out = /* @__PURE__ */ new Set();
  for (const id of chainIds) {
    const chain = id ? toCanonicalChainId(id) ?? id : "";
    if (chain) out.add(chain);
  }
  return out;
}
function derive(group, held) {
  const timedOut = group.state === "timed_out";
  if (!group.awaiting && !timedOut) return [];
  const idText = group.idLabel ? sagaIdLabel(group.idLabel).toLowerCase() : "correlation id";
  return [...new Set((group.missingRoles ?? []).map(canonRole).filter(Boolean))].map((role) => {
    const { chains, basis } = chainsFor(held, role, group.expectedRoles);
    const delivery = deliveryFor(held.highlights, role);
    return {
      role,
      reach: legReach(chains),
      basis: chains.length ? basis : void 0,
      ...delivery ? { delivery } : {},
      joinsWhen: `a ${role} transaction carrying the same ${idText} is processed`,
      expiresAt: group.expiresAt ?? void 0,
      timedOut
    };
  });
}
function expectedLegs(group, options = {}) {
  return derive(
    group,
    {
      chains: heldChains(group.legs.map((leg) => leg.chainId)),
      scenes: [group.scene],
      // One merged scene: every leg's hop is drawn in it.
      hops: group.legs.map((leg) => ({
        scene: group.flowScene ?? null,
        semanticScene: group.scene,
        txId: leg.txHash ?? "",
        txChain: leg.chainId
      })),
      highlights: group.highlights,
      declared: options.declared ?? []
    }
  );
}
function expectedLegsFromHeldLegs(group, legs, options = {}) {
  return derive(
    {
      idLabel: group.id_label,
      expiresAt: group.expires_at,
      state: group.state,
      awaiting: group.awaiting,
      missingRoles: group.missing_roles,
      expectedRoles: group.expected_roles
    },
    {
      chains: heldChains(legs.map((leg) => leg.chainId)),
      scenes: legs.map((leg) => leg.scene),
      hops: legs.map((leg) => ({
        scene: leg.flowScene ?? null,
        semanticScene: leg.scene ?? null,
        txId: leg.txHash ?? "",
        txChain: leg.chainId ?? null
      })),
      highlights: legs.flatMap((leg) => leg.highlights ?? []),
      declared: [
        ...legs.map((leg) => ({ chain: leg.remoteChainId, role: leg.role })),
        ...options.declared ?? []
      ]
    }
  );
}
function expectedLegsFromHeldLeg(group, leg) {
  return expectedLegsFromHeldLegs(group, [leg]);
}
var UNPLACED = Object.freeze({ kind: "unknown", chain: null });
function expectedLegsReach(legs) {
  return legs.find((leg) => leg.reach.kind === "uncovered")?.reach ?? legs.find((leg) => leg.reach.kind === "fetchable")?.reach ?? UNPLACED;
}
var BASIS_SENTENCE = {
  stamped: "Resolved by the grouping engine",
  declared: "Stated by the protocol on the leg we hold",
  decoded: "Read from the held leg's own decode"
};
function expectedLegSublabel(leg) {
  if (!leg.reach.chain) return void 0;
  const name = chainDisplayName(leg.reach.chain);
  return leg.reach.kind === "uncovered" ? `on ${name} \xB7 not indexed` : `on ${name}`;
}
function expectedLegFacts(leg, nowMs = Date.now()) {
  const facts = [];
  const fact = (key, label, value, kind = null) => {
    facts.push({ key, label, kind, value });
  };
  if (leg.reach.chain) {
    fact("expected_on", "Expected on", leg.reach.chain, "chain");
    if (leg.basis) fact("expected_on_basis", "Chain from", BASIS_SENTENCE[leg.basis]);
    if (leg.reach.kind === "uncovered") {
      fact(
        "expected_reach",
        "Can it arrive",
        `The missing leg ${missingLegReason(leg.reach)}`
      );
    }
  } else {
    fact("expected_on", "Expected on", "Not stated by the protocol or the held leg");
  }
  if (leg.delivery?.recipient) fact("expected_recipient", "Should pay", leg.delivery.recipient, "address");
  if (leg.delivery?.amount) {
    fact("expected_amount", leg.delivery.minimum ? "At least" : "Amount", leg.delivery.amount);
  }
  fact("joins_when", "Joins when", leg.joinsWhen.charAt(0).toUpperCase() + leg.joinsWhen.slice(1));
  if (leg.expiresAt) {
    const when = `${formatRelativeTime(leg.expiresAt, { nowMs })} (${formatTimestamp(leg.expiresAt)})`;
    fact(
      "stops_waiting",
      leg.timedOut || leg.expiresAt * 1e3 <= nowMs ? "Stopped waiting" : "Stops waiting",
      when
    );
  }
  return facts;
}
var MESSAGE_KEYS = ["message_id", "linkage_id"];
function messageProtocol(node) {
  const fact = node.facts.find((f) => MESSAGE_KEYS.includes(f.key));
  if (fact) return fact.protocol_id ?? null;
  return MESSAGE_KEYS.some((key) => node.attributes?.[key]) ? null : void 0;
}
function messageAnchor(scene, protocolId) {
  const carriers = scene.nodes.filter(
    (node) => !isExpectedNode(node) && messageProtocol(node) !== void 0
  );
  const rank = (node) => (protocolId && messageProtocol(node) === protocolId ? 0 : 2) + (node.detail_tier === "compact" ? 0 : 1);
  return [...carriers].sort((a, b) => rank(a) - rank(b))[0]?.id;
}
function annotateExpectedLegs(scene, legs, nowMs = Date.now(), protocolId) {
  if (legs.length === 0) return scene;
  const byRole = new Map(legs.map((leg) => [leg.role, leg]));
  let changed = false;
  const nodes = scene.nodes.map((node) => {
    if (!isExpectedNode(node)) return node;
    const leg = byRole.get(nodeRole(node) || (legs.length === 1 ? legs[0].role : ""));
    if (!leg) return node;
    changed = true;
    const own = new Set(expectedLegFacts(leg, nowMs).map((f) => f.key));
    return {
      ...node,
      // A group that has stopped waiting must not draw a node that still says
      // it is. The scene was written while the group was open, so the label is
      // re-said here from the group's state: the page badge read "Timed out"
      // and the legend chip "never arrived" over a node titled "Awaiting
      // destination · expected". The marker attribute keeps it a placeholder
      // for everything that asks (`isExpectedNode`), whatever its badge says.
      ...leg.timedOut ? {
        label: `${roleLabel(leg.role) || "Leg"} never arrived`,
        badge: "timed out",
        attributes: { ...node.attributes, expected: true }
      } : {},
      sublabel: expectedLegSublabel(leg) ?? node.sublabel ?? null,
      chain_id: node.chain_id ?? leg.reach.chain ?? null,
      facts: [...expectedLegFacts(leg, nowMs), ...node.facts.filter((f) => !own.has(f.key))]
    };
  });
  if (!changed) return scene;
  const ghosts = new Set(nodes.filter(isExpectedNode).map((node) => node.id));
  const hubs = new Set(scene.nodes.filter((node) => node.off_chain && !isExpectedNode(node)).map((node) => node.id));
  const anchor = messageAnchor(scene, protocolId);
  const stopped = new Map(
    nodes.filter((node) => isExpectedNode(node) && node.badge === "timed out").map((node) => [node.id, node.label])
  );
  const edges = anchor || stopped.size > 0 ? scene.edges.map((edge) => {
    const toGhost = ghosts.has(edge.target) && !ghosts.has(edge.source);
    const fromGhost = ghosts.has(edge.source) && !ghosts.has(edge.target);
    if (!toGhost && !fromGhost) return edge;
    const said = stopped.get(toGhost ? edge.target : edge.source);
    const captioned = said && /^awaiting\b/i.test(edge.label ?? "") ? { ...edge, label: said } : edge;
    if (!anchor || hubs.has(toGhost ? edge.source : edge.target)) return captioned;
    return toGhost ? { ...captioned, source: anchor } : { ...captioned, target: anchor };
  }) : scene.edges;
  return { ...scene, nodes, edges };
}

// src/grouping/reads.ts
var GROUP_PAGE_SIZE = 100;
var MAX_GROUP_PAGES = 5;
async function walkGroups(client, { sagaKind, scopeParams, signal, resource = "groups", maxPages = MAX_GROUP_PAGES, cursor: from, order } = {}) {
  const rows = [];
  let cursor = from ?? null;
  for (let page = 0; page < maxPages; page += 1) {
    const query = {
      ...scopeParams ?? {},
      limit: String(GROUP_PAGE_SIZE),
      ...sagaKind ? { saga_kind: sagaKind } : {},
      ...order && sagaKind ? { order } : {},
      ...cursor ? { cursor } : {}
    };
    const payload = unwrapData(await client.get(`/grouping/${resource}`, { query, signal }));
    rows.push(...rowsFromWire(payload));
    const next = isRecord(payload) ? payload.next_cursor : null;
    cursor = typeof next === "string" && next ? next : null;
    if (!cursor) return { rows: collapseGroupPages(rows), truncated: false, cursor: null };
  }
  return { rows: collapseGroupPages(rows), truncated: true, cursor };
}
function collapseGroupPages(rows) {
  const merged = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key = row.saga_key ?? row.id ?? row.group_id ?? row;
    const previous = merged.get(key);
    if (!previous || Number(row.read_precedence ?? -1) > Number(previous.read_precedence ?? -1)) merged.set(key, row);
  }
  return [...merged.values()];
}

export { GROUPING_COMPARATORS, GROUPING_SCOPES, GROUP_PAGE_SIZE, GroupingRuleSchema, LEG_SOURCE_KIND, MAX_GROUP_PAGES, annotateExpectedLegs, approveThenSendRule, collapseGroupPages, evaluateRule, evaluateRules, expectedLegFacts, expectedLegSublabel, expectedLegs, expectedLegsFromHeldLeg, expectedLegsFromHeldLegs, expectedLegsReach, expectedRemoteChains, legReach, messageAnchor, missingLegLabel, missingLegReason, stepMatches, validateGroupingRule, walkGroups };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map